import { TimestampsContext } from 'global/cnr/contexts/TimestampsContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import { useMatchDayResults } from 'projectSpecific/sports/useHelpers/useMatchDayResults';
import { _matchResultSourceTypes } from 'projectSpecific/sports/useHelpers/useMatchResults';
import { useRealtimeResults } from 'projectSpecific/sports/useHelpers/useRealtimeResults';
import { useSportsRealtimeResults } from 'projectSpecific/sports/useHelpers/useSportRealtimeResults';
import { useSportResults } from 'projectSpecific/sports/useHelpers/useSportResults';
import { _sportsInfoTypes, useSportsInfo } from 'projectSpecific/sports/useHelpers/useSportsInfo';
import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { FrameworkContext } from '../../../../global/cnr/contexts/FrameworkContent';
import { HeaderContext } from '../../../../global/cnr/contexts/HeaderContext';
import { ParentContext } from '../../../../global/cnr/contexts/ParentContext';
import { TrackerContext } from '../../../../global/cnr/contexts/TrackerContext';
import { _currentUserIdProp } from '../../../../global/cnr/reducers/AppUserReducer';
import { frameworkRightTypes } from '../../../../global/cnr/reducers/FrameworkReducer';
import { gEnums } from '../../../../global/enums/globalEnums';
import { currentHelpers } from '../../../../global/redirection/current';
import { sportsSeasonHandlers, sportsSeasonInitialState, sportsSeasonReducer } from '../reducers/SportsSeasonReducer';
import { SportsDistrictContext } from './SportsDistrictContext';
import { SportsOrganizationContext } from './SportsOrganizationContext';
import { AppTimestampContext } from 'global/cnr/contexts/AppTimestampContext';
import { useRenders } from 'global/cnr/contexts/useHelpers/useRenders';
import { _showAppLogs } from 'global/cnr/contexts/LogContext';
import { useSportMatches } from 'projectSpecific/sports/useHelpers/useSportMatches';

const _sportSeasonSettings = {
  useScoreBlock: false,
  useScoreTeamBlock: false,
  startHour: 19,
  usePlayoffBrackets: true,
}

const _sportsButtonProps = {
  color: 'teal',
  icon: 'snowflake outline',
}

const _sportFormProps = {
  access: {
    accessLevel: 'accessLevel',
  },
  glsAccess: {
    googleSheetsId: 'googleSheetsId',
    schoolsAccessSheet: 'schoolsAccessSheet',
    sportsAccessSheet: 'sportsAccessSheet',
    teamsAccessSheet: 'teamsAccessSheet',
    schoolsContactSheet: 'schoolsContactSheet',
    districtContactSheet: 'districtContactSheet',
  },
  glsDistrict: {
    googleSheetsId: 'googleSheetsId',
  },
  glsScores: {
    districtLink: 'districtLink',
    googleSheetsId: 'googleSheetsId',
    rankingsGoogleSheetsId: 'rankingsGoogleSheetsId',
    rosterGoogleSheetsId: 'rosterGoogleSheetsId',
    mediaUrl: 'mediaUrl',
    mediaUrlYearReplace: 'mediaUrlYearReplace',
    mediaUrlDateReplace: 'mediaUrlDateReplace',
    playoffScoresSheet: 'playoffScoresSheet',
    rankingsSheet: 'rankingsSheet',
    scoresSheet: 'scoresSheet',
    validAdmins: 'validAdmins',
    scoreEmails: 'scoreEmails',
    allowScoreUpdateFromGs: 'allowScoreUpdateFromGs',
    allowScheduledScoreUpdateFromGs: 'allowScheduledScoreUpdateFromGs',
    sortStandingsByPoints: 'sortStandingsByPoints',
  },
  rankingLinks: {
    googleSheetsId: 'googleSheetsId',
    rankingsSheet: 'rankingsSheet',
  }
}

export const _sportsFormProps = {
  access: {
    accessLevel: 'accessLevel',
  },
  accessTeam: {
    accessTeam: 'accessTeam',
  },
  accessSports: {
    accessSports: 'accessSports',
  },
  scoreAccess: {
    scoreAccess: 'scoreAccess',
  },
  accessSchools: {
    accessSchools: 'accessSchools',
  },
  contactsSchools: {
    email: 'email',
    pushApi: 'pushApi',
    pullUrl: 'pullUrl',
    hudlUrl: 'hudlUrl',
  },
  contactsDistrict: {
    email: 'email',
    pushApi: 'pushApi',
    pullUrl: 'pullUrl',
  },
  emailSchools: {
    pushApi: 'email',
  },
  googleSheetKeys: {
    googleSheetsId: 'googleSheetsId',
    rosterSheet: 'rosterSheet',
    staffSheet: 'staffSheet',
    newsSheet: 'newsSheet',
  },
  teamLinks: {
    hudlLink: 'hudlLink',
    instagramLink: 'instagramLink',
    xLink: 'xLink',
    website: 'website',
    youTubeLink: 'youTubeLink',
  },
  sportsSettings: {
    allowScoreUpdateFromGs: 'allowScoreUpdateFromGs',
  },
  tournament: {
    name: 'name',
    startDate: 'startDate',
    startTime: 'startTime',
    results: 'results',
    url: 'url',
    link: 'link',
  },
  news: {
    title: 'title',
    body: 'body',
  },
  updateDistrictSeasonScores: { ..._sportFormProps.glsScores },
  glsAccess: { ..._sportFormProps.glsAccess },
  glsDistrict: { ..._sportFormProps.glsDistrict },
  glsScores: { ..._sportFormProps.glsScores },
  scoreLink: { ..._sportFormProps.glsScores },
  seasonResults: { ..._sportFormProps.glsScores },
  seasonRankings: { ..._sportFormProps.glsScores },
  useasonRankings: { ..._sportFormProps.rankingLinks },
  createSeasonFromGs: {
    googleSheetsId: 'googleSheetsId',
    sheetName: 'sheetName',
  }
}

export const matchScheduleTypes = {
  date: 'date',
  latest: 'latest',
  next: 'next',
  overview: 'overview',
  levelMatches: 'levelMatches'
}

export const SportsSeasonContext = React.createContext()

export default function SportsSeasonProvider(props) {

  // parentContext
  const parentContext = useContext(ParentContext)
  const { states, handlers, parent_fns } = parentContext ?? {}
  const { appUser_state, paps_state, currentSettings_state, page_state, serviceWorker_state } = states ?? {}
  const { appUser_handlers } = handlers ?? {}
  const { view, pathViews, viewKey } = paps_state ?? {}
  const { sports: sports_key } = pathViews ?? {}

  // appUserContext
  const { appUser, accessClone } = appUser_state ?? {}
  const { uid, email: email_appUser } = appUser ?? {}
  const appUserAccess = useAppUserAccess()
  const { isAdminOrSuper, isAppUser, accessLevel } = appUserAccess ?? {}
  const _appUserCanModify = accessLevel >= gEnums.accessLevels.appProfile.value

  const appTimestampContext = useContext(AppTimestampContext);
  const { appTimestamp_state } = appTimestampContext ?? {}
  const { timestampInfo } = appTimestamp_state ?? {}
  const { timestamps } = timestampInfo ?? {}

  const _sits_seasonMatches = _sportsInfoTypes.sportsSeasonMatches + 'Info';
  const _sits_seasonResults = _sportsInfoTypes.sportsSeasonResults + 'Info';

  // this will refresh automatically
  const db_ts_seasonMatches = timestamps?.[_sits_seasonMatches]?.[sports_key];
  const db_ts_seasonResults = timestamps?.[_sits_seasonResults]?.[sports_key];

  // console.log('ts_seasonMatches', _sits_seasonMatches, sports_key, ts_seasonMatches)
  // console.log('ts_seasonResults', _sits_seasonResults, sports_key, ts_seasonResults)

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { frameworkRightType } = framework_state ?? {}

  const trackerContext = useContext(TrackerContext)
  const { tracker_state, tracker_handlers } = trackerContext ?? {}
  const { handle_track } = tracker_handlers ?? {}

  const timestampsContext = useContext(TimestampsContext)
  const { timestamps_handlers } = timestampsContext ?? {}

  // sportsOrganizationContext
  const sportsOrganizationContext = useContext(SportsOrganizationContext)
  const { sportsOrganization_state } = sportsOrganizationContext ?? {}
  const { organization, districts: districts_org, schools: schools_org, useAppDarkMode } = sportsOrganization_state ?? {}

  // sportsDistrictContext
  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}
  const { district, district_data, sportsAccess_district } = sportsDistrict_state ?? {}
  const { sports, schools: schools_district, gls_scores, sportsTeams, sportsAccess_sports: sportsAccess_sports_d } = district_data ?? {}
  const { sports: access_sports, schools: access_schools } = sportsAccess_district ?? {}

  const sport = sports?.[sports_key] || null
  const { name } = sport ?? {}
  const sportTeams = sportsTeams && sportsTeams[sports_key] ? sportsTeams[sports_key].teams : {}

  // pageContext 
  const { aps_global, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});
  const { sportData: sportDataSettings, sportSettings, sportPermissions } = aps_global ?? {}

  const _currentUserId = currentHelpers.storageItem_get(_currentUserIdProp)

  const { cache_info } = serviceWorker_state ?? {}
  const { cache_groups } = cache_info ?? {}
  const { cache_sport } = cache_groups ?? {}

  useRenders('SportsSeasonContext', true);

  const {
    rtAdminOnly,
    rtAppUserOnly,
    useRtMatchData,
    useRtMatchDataView,
    rtMatchUpdates,
  } = sportDataSettings ?? {}

  const getSportData = () => {

    let _rtInfo = {
      useRtMatchData: useRtMatchData,
      useRtMatchDataView: useRtMatchDataView,
      rtMatchUpdates: rtMatchUpdates,
    }

    if (rtAdminOnly) {
      _rtInfo = {
        useRtMatchData: isAdminOrSuper && useRtMatchData,
        useRtMatchDataView: isAdminOrSuper && useRtMatchDataView,
        rtMatchUpdates: isAdminOrSuper && rtMatchUpdates,
      }
    }

    if (rtAppUserOnly) {
      _rtInfo = {
        useRtMatchData: isAppUser && useRtMatchData,
        useRtMatchDataView: isAppUser && useRtMatchDataView,
        rtMatchUpdates: isAppUser && rtMatchUpdates,
      }
    }

    return _rtInfo

  }

  const headerContext = useContext(HeaderContext)
  const { header_handlers } = headerContext ?? {}

  const { viewItems: viewItems_page } = aps_page ?? {}

  let viewItem = viewItems_page && viewItems_page[view] ? viewItems_page[view] : {}
  const { dataSource } = viewItem ?? {}
  const { dataParents, useSeasonals } = dataSource ?? {}

  let ownerPath;

  if (pathViews) {
    if (pathViews.schools) { ownerPath = 'schools' }
    if (pathViews.districts) { ownerPath = 'districts' }
    if (pathViews.organizations) { ownerPath = 'organizations' }
  }

  const sportsMode = {
    organizations: pathViews && pathViews.organizations && !pathViews.districts && !pathViews.schools && !pathViews.matches && !sports_key && !pathViews.teams ? true : false,
    districts: pathViews && pathViews.districts && !pathViews.schools && !pathViews.matches && !sports_key && !pathViews.teams ? true : false,
    matches: pathViews && pathViews.matches ? true : false,
    schools: pathViews && pathViews.schools ? true : false,
    sports: pathViews && sports_key && !pathViews.teams ? true : false,
    teams: pathViews && pathViews.teams ? true : false,
    sportLanding: pathViews && sports_key && !pathViews.teams && !pathViews.matches
  }

  const _cacheData = cache_sport && cache_sport[viewKey]

  const _sportData = getSportData()

  const init_state = {
    _cacheData, // ✅
    _sportData,
    _sportSeasonSettings,
    appUserCanModify: _appUserCanModify,
    appUserAccess,
    cssOpts: { name: 'ssc-normal' },
    currentUserId: _currentUserId,
    handle_track,
    inverted: false,
    sportSettings_info: sportSettings,
    pathViews,
    sportDataSettings,
    sportPermissions,
    sportsButtonProps: _sportsButtonProps,
    sportsMode,
    uid,
    ownerPath,
    useAppDarkMode,
    useSeasonals,
    viewItem,
  }

  const [sportsSeason_state, sportsSeason_dispatch] = useReducer(sportsSeasonReducer, init_state, sportsSeasonInitialState)

  const sportsSeason_handlers = useMemo(
    () => sportsSeasonHandlers(sportsSeason_dispatch),
    [sportsSeason_dispatch]
  );

  const {
    _sportsModeKey,
    matches_info,
    sportsAccess_sports,
    sportAccess,
    sportsAccess_teams,
    currentSeason_info,
    sportsPageKey
  } = sportsSeason_state ?? {}

  const { latestSeason, sportsKey } = currentSeason_info ?? {}

  const [sportInfo, setSportInfo] = useState()
  const [sportsMatchesInfo, setSportMatchesInfo] = useState()
  const [sportsResultsInfo, setSportResultsInfo] = useState()
  const _loading = useRef(false);

  const { getSportInfoData } = useSportsInfo({ pathViews, sportsInfoType: _sportsInfoTypes.sportsSeason, sport })
  const { getSportsMatches } = useSportMatches({ pathViews, sportsInfoType: _sportsInfoTypes.sportsSeasonMatches })
  const { getSportsResults } = useSportResults({ pathViews, sportsInfoType: _sportsInfoTypes.sportsSeasonResults })

  const { matchDayResults } = useMatchDayResults({ currentSeason_info })
  const { realtimeResults } = useRealtimeResults({ currentSeason_info })

  const sportsReady = !!sports && !_.isEmpty(sport)

  const hasSportsData = useMemo(() => {
    if ((sportsPageKey === sports_key)) {
      return true
    }
    return false
  }, [sports_key, sportsPageKey, sportInfo])

  useEffect(() => {
    if (!db_ts_seasonMatches || !sports_key) return;
    getSportsMatches(_sportsInfoTypes.sportsSeasonMatches, sports_key, setSportMatchesInfo, db_ts_seasonMatches)
  }, [db_ts_seasonMatches])

  useEffect(() => {
    // get the data
    if (!sports_key || (_loading.current === sports_key)) return;
    _showAppLogs.sportsSeason && console.log('🆚👉 new sport', sports_key)
    _loading.current = sports_key;
    getSportInfoData(_sportsInfoTypes.sportsSeason, sports_key, setSportInfo)
    getSportsMatches(_sportsInfoTypes.sportsSeasonMatches, sports_key, setSportMatchesInfo, db_ts_seasonMatches)
    getSportsResults(_sportsInfoTypes.sportsSeasonResults, sports_key, setSportResultsInfo, db_ts_seasonResults)
    // getRealtimeResults(setRealtimeResults)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sports_key, sportsReady])

  useEffect(() => {
    if (hasSportsData && _.isEmpty(sportInfo) || !sports_key) return;
    _showAppLogs.sportsSeason && console.log('🟦🆚 sportInfo', sports_key, sportInfo)
    sportsSeason_handlers.handleSet_sportsInfo(sportInfo, sports_key)
    timestamps_handlers.handleAmmend_timestamps(_sportsInfoTypes.sportsSeason, sportInfo ? sportInfo._validatedTimestamp : null)
  }, [sportInfo, sports_key])

  useEffect(() => {
    if (_.isEmpty(sportsMatchesInfo) || !sports_key) return;
    _showAppLogs.sportsSeason && console.log('🟦🆚🆚 sportsMatchesInfo', sports_key, sportsMatchesInfo)
    sportsSeason_handlers.handleSet_sportsMatchesInfo(sportsMatchesInfo)
    timestamps_handlers.handleAmmend_timestamps(_sportsInfoTypes.sportsSeasonMatches, sportsMatchesInfo ? sportsMatchesInfo._validatedTimestamp : null)
  }, [sportsMatchesInfo, sports_key])

  useEffect(() => {
    if (_.isEmpty(sportsResultsInfo) || !sports_key) return;
    _showAppLogs.sportsSeason && console.log('🟦🆚🆚🆚 sportsResultsInfo', sports_key, sportsResultsInfo)
    sportsSeason_handlers.handleSet_sportsResultsInfo(sportsResultsInfo)
    const resultsMatches = (results) => _.pickBy(results, match => match?.results);
    const rrr = resultsMatches(sportsResultsInfo?.seasonResults?.results)
    timestamps_handlers.handleAmmend_timestamps(_sportsInfoTypes.sportsSeasonResults, sportsResultsInfo ? sportsResultsInfo._validatedTimestamp : null)
  }, [sportsResultsInfo, sports_key])

  useEffect(() => {
    sportsSeason_handlers.handleSet_matchDayResults(matchDayResults)
  }, [matchDayResults]);

  useEffect(() => {
    sportsSeason_handlers.handleSet_realtimeResults(realtimeResults)
  }, [realtimeResults]);

  useEffect(() => {
    if (!organization || !district || !sportInfo) return;
    const sportsParents = {
      organization: _.pick(organization, ['name', '_itemKey']),
      district: _.pick(district, ['name', '_itemKey']),
      sport: _.pick(sportInfo?.sport, ['name', '_itemKey']),
    }
    framework_handlers.handleSet_parentProps(sportsParents)
  }, [organization, district, sportInfo]);

  const findItemsByName = (obj, name) => {
    let allowedItems = {};

    function search(obj, parentKey) {
      _.forEach(obj, (value, key) => {
        if (_.isObject(value)) {
          if (value.name === name) {
            allowedItems[parentKey] = value;
          }
          search(value, key);
        }
      });
    }

    search(obj);

    return allowedItems;
  }

  useEffect(
    () => {
      if (frameworkRightType) {
        switch (frameworkRightType) {
          case frameworkRightTypes.pageMedia:
            const { levelMatches } = matches_info ?? {}
            const _matches = {
              ...levelMatches
            }
            framework_handlers.handleShow_frameworkRightData(_matches, view, viewItem.key, viewItem)
            break;
          default:
          // nothing
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, [frameworkRightType, matches_info]
  )

  useEffect(() => {
    parent_fns.addToContexts({ sportsSeason_handlers: { ...sportsSeason_handlers } })
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [])

  useEffect(() => {
    if (!_.isEmpty(districts_org)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(districts_org, 'organization', 'districts_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [districts_org])

  useEffect(() => {
    if (!_.isEmpty(schools_district)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(schools_district, 'schools', 'schools_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [schools_district])

  useEffect(() => {
    if (!_.isEmpty(sportTeams)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(sportTeams, 'sportTeams', 'teams_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sportTeams])

  useEffect(() => {
    if (latestSeason && header_handlers) {
      const _latest = `${latestSeason - 1}/${latestSeason}`;
      if (sportsMode.sportLanding) {
        header_handlers.handleSportsCaption(_latest)
      } else {
        header_handlers.handleSportsCaption(_.startCase(_sportsModeKey) + ' ' + _latest)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [latestSeason, pathViews])

  useEffect(() => {
    const { name: accessEmail } = accessClone ?? {}
    const _email = accessEmail ? accessEmail : email_appUser
    let teamsAccess;
    if (sportsAccess_teams) {
      teamsAccess = findItemsByName(sportsAccess_teams, _email)
    }
    if ((access_sports && sportsKey && access_sports[sportsKey]) || sportsAccess_sports || sportAccess || teamsAccess) {
      sportsSeason_handlers.handleSet_sportsSeasonState(access_sports && access_sports[sportsKey], 'access_sports')
      const sportsAccess = findItemsByName(access_sports, _email)
      const schoolsAccess = findItemsByName(access_schools, _email)
      const _sportAccess = findItemsByName(sportAccess, _email)
      const appUserAccess = {
        teamsAccess: _.keys(teamsAccess),
        sportsAccess: _.keys(sportsAccess),
        schoolsAccess: _.keys(schoolsAccess),
        sportAccess: _.keys(_sportAccess),
      }
      appUser_handlers.handleUpdateAccess(appUserAccess)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [sportsAccess_teams, access_sports, access_schools, sportsAccess_sports, sportsKey, accessClone])

  useEffect(() => {
    if (gls_scores && sportsKey && gls_scores[sportsKey]) {
      sportsSeason_handlers.handleSet_sportsSeasonState(gls_scores[sportsKey], 'links_score')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [gls_scores, sportsKey])

  useEffect(() => {
    if (sportsAccess_sports_d?.[sportsKey]) {
      const xxx = {
        [sportsKey]: sportsAccess_sports_d?.[sportsKey]
      }
      sportsSeason_handlers.handleSet_sportsSeasonState(xxx, 'sportAccess')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sportsAccess_sports_d, sportsKey])

  const content = () => {
    // if (loadingSport) { return <MotionWait startType={startTypes.suspense} caption={`Loading ${name ? name : 'Sport'}`} origin={'DimmerWrap'} /> }
    return <SportsSeasonContext.Provider value={{ sportsSeason_state, sportsSeason_handlers, appComponent: 'sportsSeason' }}>
      {props.children}
    </SportsSeasonContext.Provider>
  }

  return sportsSeason_state ?
    content()
    :
    <div></div>
}
